/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { Input } from "./input";
import { Button } from "./button";
import {
  AttachmentService,
  AttachmentType,
  type AttachmentDetails,
  type AttachmentEntityType,
} from "@/services/AttachmenteService";
import { type AxiosProgressEvent } from "axios";
import ImagePreviewDialog from "../ImagePreviewDialog";
import AttachmentImage from "../AttachmentImage";

interface FileUploadMainProps {
  tenantId: string;
  siteId: string;
  entityId: string;
  entityType: AttachmentEntityType;
  showMainImage?: boolean;
  onUploadComplete?: (attachmentDetails: AttachmentDetails) => void;
}

const FileUploadMain: React.FC<FileUploadMainProps> = ({ tenantId, siteId, entityId, entityType, showMainImage = true, onUploadComplete }) => {
  const [featureMainImageFile, setFeatureMainImageFile] = useState<any>(null);
  const [selectedMainImage, setSelectedMainImage] = useState<string | null>(null);
  const [mainAttachment, setMainAttachment] = useState<AttachmentDetails | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileChange = (event: any) => {
    setFeatureMainImageFile(event.target.files[0]);
  };

  useEffect(() => {
    const fetchAttachments = async () => {
      try {
        if (entityId) {
          const attachments = await AttachmentService.getByEntityId(entityId);
          const mainImage = attachments.find(
            (att) => att.type === AttachmentType.Main
          );
          setSelectedMainImage(mainImage ? mainImage.url : null);
          setMainAttachment(mainImage ?? null);
        }
      } catch (error) {
        console.error("Error fetching attachments", error);
      }
    };

    fetchAttachments();
  }, [entityId]);

  // Revoke the previous blob URL when it changes or on unmount to avoid leaking memory.
  useEffect(() => {
    return () => {
      if (selectedMainImage?.startsWith("blob:")) {
        URL.revokeObjectURL(selectedMainImage);
      }
    };
  }, [selectedMainImage]);

  const onHandleFeatureImageUpload = async () => {
    if (!(featureMainImageFile instanceof File)) {
      alert("Please select a file first!");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    try {
      const [sas] = await AttachmentService.getSasTokens(
        [featureMainImageFile.name],
        tenantId,
        siteId,
        entityId,
      );

      if (!sas) {
        throw new Error("No SAS token returned");
      }

      await AttachmentService.uploadToSasUrl(
        sas.sasToken,
        featureMainImageFile,
        {
          originalFilename: featureMainImageFile.name,
          attachmentId: sas.attachmentId,
          attachmentType: AttachmentType.Main,
          entityType,
          source: "Entity",
        },
        (progressEvent: AxiosProgressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1),
          );
          setUploadProgress(progress);
        },
      );

      const objectUrl = URL.createObjectURL(featureMainImageFile);
      const attachment: AttachmentDetails = {
        id: sas.attachmentId,
        filename: featureMainImageFile.name,
        entityId,
        url: objectUrl,
        thumbnailUrl: objectUrl,
        size: featureMainImageFile.size,
        uploadDate: new Date().toISOString(),
        type: AttachmentType.Main,
        originalFilename: featureMainImageFile.name,
        dateTaken: new Date().toISOString(),
        createdByName: "Unknown",
        isProcessing: true,
      };

      setFeatureMainImageFile(null);
      setSelectedMainImage(objectUrl);
      setMainAttachment(attachment);
      onUploadComplete?.(attachment);
    } catch (e) {
      console.error("Error uploading main feature file", e);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <>
      {isUploading ? (
        <div className="rounded-md border border-border bg-muted/40 p-3 md:max-w-62.5">
          <div className="mb-2 flex items-center justify-between gap-2 text-sm">
            <span className="truncate font-medium">{featureMainImageFile?.name}</span>
            <span className="shrink-0 tabular-nums text-muted-foreground">
              {uploadProgress > 99 ? "Finalising" : `${uploadProgress}%`}
            </span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-green-500 transition-all duration-300 ease-out"
              style={{ width: `${Math.min(uploadProgress, 100)}%` }}
            />
          </div>
        </div>
      ) : (
        <>
          <Input type="file" onChange={handleFileChange} />
          <Button
            size="sm"
            disabled={!featureMainImageFile}
            onClick={onHandleFeatureImageUpload}
            className="bg-green-400 md:max-w-62.5"
          >
            Upload File
          </Button>{" "}
        </>
      )}

      {selectedMainImage && showMainImage && (
        <div className="pt-1 md:max-w-62.5">
          <AttachmentImage
            url={selectedMainImage}
            isProcessing={mainAttachment?.isProcessing}
            alt={mainAttachment?.originalFilename ?? "main feature image"}
            className="rounded-lg cursor-pointer"
            title="main feature image"
            role="button"
            tabIndex={0}
            onClick={() => setIsPreviewOpen(true)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setIsPreviewOpen(true);
              }
            }}
          />
        </div>
      )}
      <ImagePreviewDialog
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        title="Main image"
        imageUrl={selectedMainImage}
        alt={mainAttachment?.originalFilename ?? "main feature image"}
        dateTaken={mainAttachment?.dateTaken ?? mainAttachment?.uploadDate}
        createdByName={mainAttachment?.createdByName}
        isProcessing={mainAttachment?.isProcessing}
      />
    </>
  );
};

export default FileUploadMain;
